import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as actions from '../../../store/actions/index';
import { storageEmpresaNombre } from '../../../assets/shared/sessionData';
import { nombreComprobante, terceroComprobante, texto } from '../textosComprobante';
import { claveComprobante } from './useDatosComprobante';

const mailValido = (mail) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail);

const separarMails = (valor) =>
  valor
    .split(/[;,\s]+/)
    .map((mail) => mail.trim())
    .filter((mail) => mail);

// remitente: quién firma (Clientes.Nombre del Dashboard; mientras carga, el de la empresa de la sesión).
const asuntoInicial = (comprobante, remitente) => `${remitente ? `${remitente} - ` : ''}${nombreComprobante(comprobante)}`;

const mensajeInicial = (comprobante, remitente) =>
  `Estimado/a ${texto(comprobante.nombre) || terceroComprobante(comprobante)}:\n\nAdjuntamos el comprobante ${nombreComprobante(comprobante)}.\n\nSaludos${remitente ? `,\n${remitente}` : '.'}`;

/**
 * Estado del envío del PDF de un comprobante por mail. El destinatario se precarga con los mails del cliente
 * en el Eiffel y se puede cambiar. Sale por nuestro SMTP firmado con Clientes.Nombre y las respuestas van al
 * mail del usuario del Eiffel asociado. onEnviado se llama cuando el mail salió.
 */
const useMailComprobante = (comprobante, onEnviado) => {
  const dispatch = useDispatch();
  const [destinatarios, setDestinatarios] = useState('');
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [cargandoMails, setCargandoMails] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const clave = claveComprobante(comprobante);

  useEffect(() => {
    if (!comprobante) return undefined;

    let cancelado = false;
    setDestinatarios('');
    const empresa = (storageEmpresaNombre() || '').trim();
    setAsunto(asuntoInicial(comprobante, empresa));
    setMensaje(mensajeInicial(comprobante, empresa));
    setEnviando(false);
    setCargandoMails(true);
    dispatch(
      actions.loadMailDestinatarioImpresion(
        comprobante.idCliente,
        (mails) => {
          if (cancelado) return;
          setDestinatarios(mails.join(', '));
          setCargandoMails(false);
        },
        () => !cancelado && setCargandoMails(false),
        comprobante.idSistema
      )
    );
    // Cuando llega el remitente se rearman asunto y mensaje, salvo que el usuario ya los haya cambiado.
    dispatch(
      actions.loadRemitenteImpresion((remitente) => {
        if (cancelado || remitente === empresa) return;
        setAsunto((actual) => (actual === asuntoInicial(comprobante, empresa) ? asuntoInicial(comprobante, remitente) : actual));
        setMensaje((actual) => (actual === mensajeInicial(comprobante, empresa) ? mensajeInicial(comprobante, remitente) : actual));
      })
    );

    return () => {
      cancelado = true;
    };
    // Sólo al cambiar de comprobante: el refresco del listado no tiene que pisar lo que se está escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, dispatch]);

  const mails = separarMails(destinatarios);
  const mailsInvalidos = mails.filter((mail) => !mailValido(mail));
  const puedeEnviar = !enviando && !cargandoMails && mails.length > 0 && mailsInvalidos.length === 0 && asunto.trim() !== '';

  const ayudaDestinatarios = cargandoMails
    ? `Buscando el mail del ${terceroComprobante(comprobante)}...`
    : mailsInvalidos.length > 0
      ? `Mail inválido: ${mailsInvalidos.join(', ')}`
      : mails.length === 0
        ? `El ${terceroComprobante(comprobante)} no tiene mail cargado en el Eiffel. Ingrese uno o más, separados por coma.`
        : 'Puede ingresar varios mails separados por coma.';

  const enviar = () => {
    setEnviando(true);
    dispatch(
      actions.enviarMailComprobanteImpresion(
        comprobante,
        { destinatarios: mails, asunto: asunto.trim(), mensaje },
        () => onEnviado && onEnviado(),
        () => setEnviando(false)
      )
    );
  };

  return {
    destinatarios,
    setDestinatarios,
    asunto,
    setAsunto,
    mensaje,
    setMensaje,
    cargandoMails,
    enviando,
    errorDestinatarios: !cargandoMails && (mails.length === 0 || mailsInvalidos.length > 0),
    ayudaDestinatarios,
    puedeEnviar,
    enviar
  };
};

export default useMailComprobante;
